import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { BackgroundJobsTable } from '../-components/BackgroundJobsTable';
import { backgroundJobsQueryOptions } from '../-hooks/useBackgroundJobs';
import { useBackgroundJobsPage } from '../-hooks/useBackgroundJobsPage';

const backgroundJobsSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  // The endpoint caps page size at 500 (`defaultPaginationConfig.maxLimit`).
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/background-jobs/(pages)/')({
  validateSearch: backgroundJobsSearch,
  // Read off the URL, so a deep link with a filter prefetches the rows it will
  // actually show; `backgroundJobsQueryOptions` keys on the converted body.
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed: the page reports a failed load as a banner above the table.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(backgroundJobsQueryOptions(deps))
      .catch(() => undefined),
  component: BackgroundJobsPage,
});

function BackgroundJobsPage() {
  const page = useBackgroundJobsPage();

  return (
    <PageContainer>
      <BackgroundJobsTable page={page} />
    </PageContainer>
  );
}
